import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Modal,
  FlatList,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Text,
  ActivityIndicator,
  Image,
  SafeAreaView,
  Platform,
  Dimensions,
  Alert,
} from 'react-native';
import { useSelector } from 'react-redux';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import Svg, { Path } from 'react-native-svg';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { RootState } from '../redux/store';
import { apiLink } from '../Common/common';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';
import { streamBillPhoto, streamChat } from '../services/chatStreamClient';
import moment from 'moment';
import ReportChartCard from '../Screens/ReportCharts/ReportChartCard';
import { detectReportIntent, ReportIntent } from '../Screens/ReportCharts/chatIntent';
import { ChatCard, extractCard } from './chatCard';
import ChatCardView from './ChatCardView';
import {
  useAssistantNameSync,
  loadAssistantNameFromStorage,
  saveAssistantNameToStorage,
  ASSISTANT_NAMES,
} from '../utils/assistantName';

import T from '../Components/T';
import { tp } from '../utils/translateText';
interface AIChatBotProps {
  open: boolean;
  onClose: () => void;
}

interface ChatMessage {
  id: string;
  text: string;
  images?: Array<{ url: string; alt: string }>;
  sender: 'user' | 'bot';
  timestamp: Date;
  /** Live report content (chart or report list) rendered under the text. */
  report?: ReportIntent;
  /** Confirm / choice card from the entry-action engine (staff only). */
  card?: ChatCard;
  /** Thumbnail of a bill photo the user sent (local file URI). */
  imageUri?: string;
}

interface ChatImage {
  url: string;
  alt: string;
}

// Assistant mark: three flowing wind lines (redrawn from web AIChatBot.tsx:40-51)
const AIIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path
      d="M9.6 4.6a2 2 0 1 1 1.4 3.4H2M12.6 19.4a2 2 0 1 0 1.4-3.4H2M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Image marker extraction (ported from AIChatBot.tsx:104-118)
const IMAGE_MARKER_RE = /\[\[IMAGE:([^|\]]+)\|([^\]]*)\]\]/g;

function extractImages(text: string): { text: string; images: ChatImage[] } {
  const images: ChatImage[] = [];
  const cleaned = text
    .replace(IMAGE_MARKER_RE, (_match, url: string, alt: string) => {
      images.push({ url: url.trim(), alt: alt.trim() });
      return '';
    })
    .trim();
  return { text: cleaned, images };
}

const AIChatBot: React.FC<AIChatBotProps> = ({ open, onClose }) => {
  const token = useSelector((state: RootState) => state.gdata?.token);
  const gdata = useSelector((state: RootState) => state.gdata);

  // Assistant name state
  const [assistantName, setAssistantName] = useState<string>(() =>
    useAssistantNameSync()
  );

  // Chat messages
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);

  // Streaming state
  const streamControlRef = useRef<{ cancel: () => void } | null>(null);
  const flatListRef = useRef<FlatList>(null);

  // Load saved assistant name and show welcome message on open
  useEffect(() => {
    if (!open) return;

    const loadAndInitialize = async () => {
      try {
        const savedName = await loadAssistantNameFromStorage();
        if (savedName) {
          setAssistantName(savedName);
        } else {
          // Save the newly-computed name for next time
          await saveAssistantNameToStorage(assistantName);
        }
      } catch (err) {
        console.warn('Failed to load assistant name:', err);
      }

      // Show welcome message
      if (messages.length === 0) {
        const firstName = gdata?.username?.split(' ')[0] || 'there';
        const welcomeText = `Hello ${firstName}! I'm ${assistantName}, your BlueHMS AI assistant. I can help you understand how to use the system, navigate modules, and answer questions about hotel management. How can I assist you today?`;
        setMessages([
          {
            id: '1',
            text: welcomeText,
            sender: 'bot',
            timestamp: new Date(),
          },
        ]);
      }
    };

    loadAndInitialize();
  }, [open, assistantName, gdata?.username, messages.length]);

  // Cancel streaming on unmount
  useEffect(() => {
    return () => {
      if (streamControlRef.current) {
        streamControlRef.current.cancel();
      }
    };
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (flatListRef.current && messages.length > 0) {
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 50);
    }
  }, [messages]);

  const handleSendMessage = () => (photo ? sendPhoto() : sendText(inputMessage));

  // Bill photos: shown only when the server says this user can use them (feature flag + rights).
  const [billEnabled, setBillEnabled] = useState(false);
  const [photo, setPhoto] = useState<{ uri: string; type: string; name: string } | null>(null);

  useEffect(() => {
    if (!open || !token) {
      setBillEnabled(false);
      return;
    }
    let cancelled = false;
    fetch(`${apiLink}chatbot/action/capabilities`, { headers: { auth: token } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled) setBillEnabled(!!j?.bill?.enabled);
      })
      .catch(() => {
        if (!cancelled) setBillEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, token]);

  // The picker re-encodes to JPEG and downsizes (quality/maxWidth), so no extra image library is needed.
  const pickPhoto = async (source: 'camera' | 'library') => {
    const options = { mediaType: 'photo' as const, cameraType: 'back' as const, quality: 0.8 as const, maxWidth: 2000, maxHeight: 2000, includeBase64: false };
    try {
      const result = source === 'camera' ? await launchCamera(options) : await launchImageLibrary(options);
      const asset = result.assets?.[0];
      if (result.didCancel || !asset?.uri) return;
      setPhoto({ uri: asset.uri, type: asset.type || 'image/jpeg', name: asset.fileName || 'bill.jpg' });
    } catch (e) {
      console.warn('Photo pick failed:', e);
      Alert.alert(tp("Photo"), tp("I could not open the camera or gallery."));
    }
  };

  const choosePhotoSource = () => {
    Alert.alert(tp("Bill photo"), tp("Take a photo of the purchase bill, or choose one from your gallery."), [
      { text: 'Camera', onPress: () => pickPhoto('camera') },
      { text: 'Gallery', onPress: () => pickPhoto('library') },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  // A photo of a purchase bill goes to its own endpoint (multipart). The server reads it (it can take a minute) and
  // streams back a confirm card; nothing is saved until Confirm.
  const sendPhoto = async () => {
    if (!photo || isLoading || !token) return;
    const sent = photo;
    const caption = inputMessage.trim();
    setMessages((prev) => [
      ...prev,
      { id: Date.now().toString(), text: caption || 'Bill photo', imageUri: sent.uri, sender: 'user', timestamp: new Date() },
    ]);
    setInputMessage('');
    setPhoto(null);
    setIsLoading(true);
    setIsThinking(true);

    let botMessageId = '';
    let accumulated = '';
    try {
      const { promise, cancel } = streamBillPhoto(
        sent,
        caption,
        token,
        assistantName,
        (chunk) => {
          streamControlRef.current = { cancel };
          accumulated += chunk;
          const { text: noCard, card } = extractCard(accumulated);
          const { text, images } = extractImages(noCard);
          setIsThinking(false);
          if (!botMessageId) {
            botMessageId = (Date.now() + 1).toString();
            setMessages((prev) => [...prev, { id: botMessageId, text, images, card, sender: 'bot', timestamp: new Date() }]);
          } else {
            const id = botMessageId;
            setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text, images, card } : m)));
          }
        },
        apiLink
      );
      await promise;
    } catch (error) {
      setIsThinking(false);
      console.error('Bill photo error:', error);
      // The server's own message (too large, too many photos, ...) when it sent one.
      const detail = error instanceof Error && error.message && !/status \d+/.test(error.message) ? error.message : '';
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 2).toString(),
          text: detail || 'Sorry, I could not send that photo. If the bill was already being read, send the same photo again in a minute.',
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
      streamControlRef.current = null;
    }
  };

  // Shared by the input box and the card's choice chips.
  const sendText = async (rawText: string) => {
    if (!rawText.trim() || isLoading || !token) return;

    const question = rawText.trim();
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: question,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');

    // Report questions are answered locally from the user's own menu rights and the
    // existing report APIs; everything else goes to the LLM below.
    const intent = detectReportIntent(question, gdata?.menu);
    if (intent) {
      const range = intent.kind === 'chart'
        ? `${moment(intent.fdt).format('DD MMM')}${intent.fdt === intent.tdt ? '' : ` – ${moment(intent.tdt).format('DD MMM')}`}`
        : '';
      const text =
        intent.kind === 'denied'
          ? `You don't have access to ${intent.title}. Please ask your administrator if you need it.`
          : intent.kind === 'chart'
          ? `Here is ${intent.entry.title} for ${intent.label} (${range}):`
          : intent.entries.length + intent.webOnly.length > 0
          ? 'These are the reports available to you:'
          : 'No reports are available to you.';
      setMessages((prev) => [
        ...prev,
        { id: (Date.now() + 1).toString(), text, sender: 'bot', timestamp: new Date(), report: intent },
      ]);
      return;
    }

    setIsLoading(true);
    setIsThinking(true);

    let botMessageId = '';
    let accumulated = '';

    try {
      const { promise, cancel } = streamChat(
        question,
        token,
        assistantName,
        (chunk) => {
          // Store cancel for unmount
          streamControlRef.current = { cancel };

          accumulated += chunk;
          // Strip an entry-action card marker first (a half-received one stays hidden), then images.
          const { text: noCard, card } = extractCard(accumulated);
          const { text, images } = extractImages(noCard);
          setIsThinking(false);

          if (!botMessageId) {
            botMessageId = (Date.now() + 1).toString();
            const newBotMessage: ChatMessage = {
              id: botMessageId,
              text,
              images,
              card,
              sender: 'bot',
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, newBotMessage]);
          } else {
            const id = botMessageId;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === id ? { ...m, text, images, card } : m
              )
            );
          }
        },
        apiLink
      );

      await promise;
    } catch (error) {
      setIsThinking(false);
      console.error('Chat error:', error);
      // Show error message to user
      const errorId = (Date.now() + 2).toString();
      setMessages((prev) => [
        ...prev,
        {
          id: errorId,
          text: "Sorry, I encountered an error while processing your request. Please try again.",
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
      streamControlRef.current = null;
    }
  };

  const renderMessage = ({ item }: { item: ChatMessage }) => {
    const isUser = item.sender === 'user';
    return (
      <View
        style={[
          styles.messageBubbleContainer,
          isUser ? styles.userBubbleContainer : styles.botBubbleContainer,
        ]}
      >
        <View
          style={[
            styles.messageBubble,
            isUser ? styles.userBubble : styles.botBubble,
          ]}
        >
          {item.imageUri ? (
            <Image source={{ uri: item.imageUri }} style={styles.billThumb} resizeMode="cover" />
          ) : null}
          <Text
            style={[
              styles.messageText,
              isUser ? styles.userText : styles.botText,
            ]}
          >
            {item.text}
          </Text>

          {item.report?.kind === 'chart' && (
            <View style={{ marginTop: 8, width: Dimensions.get('window').width - 40 }}>
              <ReportChartCard
                entry={item.report.entry}
                cid={gdata.company}
                fdt={item.report.fdt}
                tdt={item.report.tdt}
                onNavigate={onClose}
              />
            </View>
          )}
          {item.report?.kind === 'list' && (
            <View style={{ marginTop: 8 }}>
              {item.report.entries.map((e) => (
                <Text key={e.key} style={[styles.messageText, styles.botText]}>
                  {'•'} {e.title}
                  {e.screen ? '' : ' (chart here, full report on web)'}
                </Text>
              ))}
              {item.report.webOnly.map((n) => (
                <Text key={n} style={[styles.messageText, styles.botText]}>
                  {'•'} {n} <T>(web)</T>
                </Text>
              ))}
              <Text style={[styles.messageText, styles.botText, { marginTop: 6 }]}>
                <T>Ask e.g. "show revenue this month" to see a chart.</T>
              </Text>
            </View>
          )}

          {item.card && token ? (
            <ChatCardView
              card={item.card}
              apiLink={apiLink}
              token={token}
              active={item.id === messages[messages.length - 1]?.id && !isLoading}
              onChoose={sendText}
            />
          ) : null}

          {item.images && item.images.length > 0 && (
            <View style={styles.imagesContainer}>
              {item.images.map((img, idx) => (
                <Image
                  key={idx}
                  source={{ uri: img.url }}
                  style={styles.inlineImage}
                  resizeMode="contain"
                />
              ))}
            </View>
          )}
        </View>
      </View>
    );
  };

  const keyExtractor = (item: ChatMessage) => item.id;

  return (
    <Modal
      visible={open}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <SafeAreaView style={styles.modalContainer}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <AIIcon size={28} color="#2563eb" />
            <Text style={styles.headerTitle}>{assistantName}</Text>
          </View>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <FontAwesome name="close" size={24} color="#333" />
          </TouchableOpacity>
        </View>

        {/* Messages */}
        <FlatList
          ref={flatListRef}
          data={messages}
          renderItem={renderMessage}
          keyExtractor={keyExtractor}
          contentContainerStyle={styles.messagesContent}
          scrollEnabled={true}
        />

        {/* Thinking indicator */}
        {isThinking && (
          <View style={styles.thinkingContainer}>
            <ActivityIndicator size="small" color="#2563eb" />
            <Text style={styles.thinkingText}><T>Thinking...</T></Text>
          </View>
        )}

        {/* Photo waiting to be sent */}
        {photo ? (
          <View style={styles.photoPreview}>
            <Image source={{ uri: photo.uri }} style={styles.photoPreviewImage} resizeMode="cover" />
            <Text style={styles.photoPreviewText}><T>Bill photo ready. Add a note if you like, then send.</T></Text>
            <TouchableOpacity onPress={() => setPhoto(null)} style={styles.photoRemove}>
              <FontAwesome name="close" size={18} color="#666" />
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Input area */}
        <View style={styles.inputArea}>
          {billEnabled ? (
            <TouchableOpacity
              style={styles.cameraButton}
              onPress={choosePhotoSource}
              disabled={isLoading}
              accessibilityLabel="Attach a bill photo"
            >
              <FontAwesome name="camera" size={20} color={isLoading ? '#ccc' : '#2563eb'} />
            </TouchableOpacity>
          ) : null}
          <View style={styles.inputWrapper}>
            <TextInput
              style={styles.input}
              placeholder={photo ? 'Add a note about this bill (optional)...' : 'Ask me anything...'}
              placeholderTextColor="#999"
              value={inputMessage}
              onChangeText={setInputMessage}
              editable={!isLoading}
              multiline
            />
          </View>
          <TouchableOpacity
            style={[
              styles.sendButton,
              (isLoading || (!inputMessage.trim() && !photo)) && styles.sendButtonDisabled,
            ]}
            onPress={handleSendMessage}
            disabled={isLoading || (!inputMessage.trim() && !photo)}
          >
            <FontAwesome
              name="send"
              size={20}
              color={isLoading || (!inputMessage.trim() && !photo) ? '#ccc' : '#2563eb'}
            />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  inputWrapper: {
    flex: 1,
    maxHeight: 80,
  },
  cameraButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  billThumb: {
    width: 160,
    height: 160,
    borderRadius: 8,
    marginBottom: 6,
  },
  photoPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingTop: 8,
    gap: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    backgroundColor: '#fff',
  },
  photoPreviewImage: {
    width: 48,
    height: 48,
    borderRadius: 6,
  },
  photoPreviewText: {
    flex: 1,
    fontSize: 12,
    color: '#6b7280',
  },
  photoRemove: {
    padding: 6,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    backgroundColor: '#fff',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
  },
  closeButton: {
    padding: 8,
  },
  messagesContent: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  messageBubbleContainer: {
    marginVertical: 8,
    flexDirection: 'row',
  },
  userBubbleContainer: {
    justifyContent: 'flex-end',
  },
  botBubbleContainer: {
    justifyContent: 'flex-start',
  },
  messageBubble: {
    maxWidth: '85%',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
  },
  userBubble: {
    backgroundColor: '#2563eb',
  },
  botBubble: {
    backgroundColor: '#f3f4f6',
  },
  messageText: {
    fontSize: 14,
    lineHeight: 20,
  },
  userText: {
    color: '#fff',
  },
  botText: {
    color: '#1f2937',
  },
  imagesContainer: {
    marginTop: 8,
    gap: 4,
  },
  inlineImage: {
    width: '100%',
    height: 150,
    borderRadius: 8,
  },
  thinkingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  thinkingText: {
    fontSize: 14,
    color: '#666',
  },
  inputArea: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    alignItems: 'flex-end',
    gap: 8,
    backgroundColor: '#fff',
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    maxHeight: 80,
    color: '#1f2937',
  },
  sendButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
});

export default AIChatBot;
